import type { Coord } from "@dailywars/core/engine";
import type { RefObject } from "react";

import { TILE_SIZE } from "#/game/canvasRenderer";

export const MIN_TILE_PX_FINE = 44;
export const MIN_TILE_PX_COARSE = 48;

export type ScrollChromeRefs = {
  topDockRef: RefObject<HTMLElement | null>;
  bottomDockRef: RefObject<HTMLElement | null>;
};

const SCROLL_INTO_VIEW_MARGIN = 8;

const MIN_BOARD_VIEWPORT_TILES = 6;
export const MIN_BOARD_VIEWPORT_PX = MIN_BOARD_VIEWPORT_TILES * TILE_SIZE;
export const MIN_BOARD_VIEWPORT_HEIGHT_PX = MIN_BOARD_VIEWPORT_TILES * TILE_SIZE;

export const MIN_ZOOM = 0.6;
export const MAX_ZOOM = 1;

export const BOARD_EDGE_MARGIN_PX = 12;

export function boardContentWidth({
  containerWidth,
  edgeMarginPx,
}: {
  containerWidth: number;
  edgeMarginPx: number;
}): number {
  return Math.max(0, containerWidth - edgeMarginPx * 2);
}

export function clampZoom(zoom: number, minZoom = MIN_ZOOM): number {
  if (!Number.isFinite(zoom)) return 1;
  return Math.min(MAX_ZOOM, Math.max(minZoom, zoom));
}

export function computePinchZoom({
  startZoom,
  startDistance,
  distance,
  minZoom = MIN_ZOOM,
}: {
  startZoom: number;
  startDistance: number;
  distance: number;
  minZoom?: number;
}): number {
  if (startDistance <= 0) return clampZoom(startZoom, minZoom);
  return clampZoom((startZoom * distance) / startDistance, minZoom);
}

export function computePinchScrollTargets({
  startZoom,
  zoom,
  focal,
  start,
}: {
  startZoom: number;
  zoom: number;
  focal: { x: number; y: number };
  start: {
    scrollLeft: number;
    windowScrollY: number;
    viewportLeft: number;
    canvasTop: number;
  };
}): { scrollLeft: number; windowScrollY: number } {
  const ratio = startZoom > 0 ? zoom / startZoom : 1;
  const focalX = focal.x - start.viewportLeft;
  const focalY = focal.y - start.canvasTop;
  return {
    scrollLeft: (start.scrollLeft + focalX) * ratio - focalX,
    windowScrollY: start.windowScrollY + focalY * (ratio - 1),
  };
}

export function scrollVisibleYBounds({
  viewportHeight,
  visualViewportOffsetTop = 0,
}: {
  viewportHeight: number;
  visualViewportOffsetTop?: number;
}): { top: number; bottom: number } {
  return {
    top: visualViewportOffsetTop + SCROLL_INTO_VIEW_MARGIN,
    bottom: visualViewportOffsetTop + viewportHeight - SCROLL_INTO_VIEW_MARGIN,
  };
}

export function scrollVisibleYBoundsFromChrome({
  viewportHeight,
  visualViewportOffsetTop = 0,
  topDockEl = null,
  bottomDockEl = null,
}: {
  viewportHeight: number;
  visualViewportOffsetTop?: number;
  topDockEl?: HTMLElement | null;
  bottomDockEl?: HTMLElement | null;
}): { top: number; bottom: number } {
  const { top, bottom } = scrollVisibleYBounds({ viewportHeight, visualViewportOffsetTop });
  return {
    top: topDockEl
      ? Math.max(top, topDockEl.getBoundingClientRect().bottom + SCROLL_INTO_VIEW_MARGIN)
      : top,
    bottom: bottomDockEl
      ? Math.min(bottom, bottomDockEl.getBoundingClientRect().top - SCROLL_INTO_VIEW_MARGIN)
      : bottom,
  };
}

function readScrollChromeElements(scrollChrome?: ScrollChromeRefs): {
  topDockEl: HTMLElement | null;
  bottomDockEl: HTMLElement | null;
} {
  if (!scrollChrome) {
    return { topDockEl: null, bottomDockEl: null };
  }
  return {
    topDockEl: scrollChrome.topDockRef.current,
    bottomDockEl: scrollChrome.bottomDockRef.current,
  };
}

export function computeDisplayScale({
  containerWidth,
  intrinsicWidth,
  minTilePx,
}: {
  containerWidth: number;
  intrinsicWidth: number;
  minTilePx: number;
}): number {
  if (containerWidth <= 0 || intrinsicWidth <= 0) return 1;
  const minScale = minTilePx / TILE_SIZE;
  if (intrinsicWidth > containerWidth) {
    return 1;
  }
  const scaleToFit = containerWidth / intrinsicWidth;
  return Math.min(1, Math.max(scaleToFit, minScale));
}

export function computeInitialTouchZoom({
  containerWidth,
  intrinsicWidth,
  baseScale,
  edgeMarginPx = 0,
}: {
  containerWidth: number;
  intrinsicWidth: number;
  baseScale: number;
  edgeMarginPx?: number;
}): number {
  if (containerWidth <= 0 || intrinsicWidth <= 0 || baseScale <= 0) return MIN_ZOOM;
  const targetCssWidth = boardContentWidth({ containerWidth, edgeMarginPx });
  if (targetCssWidth <= 0) return MIN_ZOOM;
  return clampZoom(targetCssWidth / (intrinsicWidth * baseScale));
}

function scrollViewportXIfNeeded({
  viewport,
  tileLeft,
  tileSizePx,
}: {
  viewport: HTMLDivElement;
  tileLeft: number;
  tileSizePx: number;
}): void {
  const visL = viewport.scrollLeft;
  const visR = visL + viewport.clientWidth;
  const fullyVisibleX = tileLeft >= visL && tileLeft + tileSizePx <= visR;
  if (fullyVisibleX) return;
  const tileCenterX = tileLeft + tileSizePx / 2;
  const maxLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  const targetLeft = tileCenterX - viewport.clientWidth / 2;
  viewport.scrollLeft = Math.min(maxLeft, Math.max(0, targetLeft));
}

function scrollWindowYIfTileObscured({
  canvas,
  tileTop,
  tileSizePx,
  scrollChrome,
}: {
  canvas: HTMLCanvasElement;
  tileTop: number;
  tileSizePx: number;
  scrollChrome?: ScrollChromeRefs;
}): void {
  const vv = window.visualViewport;
  const vh = vv?.height ?? window.innerHeight;
  const topInset = vv?.offsetTop ?? 0;
  const { topDockEl, bottomDockEl } = readScrollChromeElements(scrollChrome);
  const { top: visibleTop, bottom: visibleBottom } = scrollVisibleYBoundsFromChrome({
    viewportHeight: vh,
    visualViewportOffsetTop: topInset,
    topDockEl,
    bottomDockEl,
  });
  if (visibleBottom <= visibleTop) return;

  const canvasRect = canvas.getBoundingClientRect();
  const tileScreenTop = canvasRect.top + tileTop;
  const tileScreenBottom = tileScreenTop + tileSizePx;
  if (tileScreenTop >= visibleTop && tileScreenBottom <= visibleBottom) return;

  const tileCenterY = tileScreenTop + tileSizePx / 2;
  const visibleMid = (visibleTop + visibleBottom) / 2;
  window.scrollBy({ top: tileCenterY - visibleMid, behavior: "auto" });
}

export function computePanelBottomAboveChrome({
  containerBottom,
  dockTop,
  gap = 8,
}: {
  containerBottom: number;
  dockTop: number;
  gap?: number;
}): number {
  return Math.max(gap, containerBottom - (dockTop - gap));
}

export function scrollTileIntoView({
  viewport,
  canvas,
  focus,
  displayScale,
  scrollChrome,
}: {
  viewport: HTMLDivElement;
  canvas: HTMLCanvasElement;
  focus: Coord;
  displayScale: number;
  scrollChrome?: ScrollChromeRefs;
}): void {
  const s = TILE_SIZE * displayScale;
  const tileLeft = focus.x * s;
  scrollViewportXIfNeeded({ viewport, tileLeft, tileSizePx: s });
  scrollWindowYIfTileObscured({ canvas, tileTop: focus.y * s, tileSizePx: s, scrollChrome });
}
